import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { Gender, type Maybe } from '@xing-com/crate-common-graphql-types';
import { Facepile } from '@xing-com/facepile';
import { IconArrowRight } from '@xing-com/icons';
import { BodyCopy } from '@xing-com/typography';

import { getFollowersContactsKey } from '../../helpers';
import { useUserLoginState } from '../../hooks/user-login-state';
import { type ArticleAuthorType } from '../../types';

import ArticleAuthorFollowers from './article-author-followers';
import { Styled } from './styles';

const MAX_FACEPILE_ACTORS = 5;

const getViewArticlesURL = (author: ArticleAuthorType): string => {
  if (author.type === 'content_page') {
    return `${author.pageURL}?sc_o=news_articledetailpage_author_articles_link`;
  }
  return `/news/insider-collection/${author.pageName}?sc_o=news_articledetailpage_author_articles_link`;
};

type ArticleAuthorInfoBoxProps = {
  articleId: string;
  author: Maybe<ArticleAuthorType>;
};

const ArticleAuthorInfoBox: React.FC<ArticleAuthorInfoBoxProps> = ({
  articleId,
  author,
}) => {
  const intl = useIntl();
  const { isLoggedOut } = useUserLoginState();

  const [showFollowersContacts, setShowFollowersContacts] = useState<boolean>();

  if (!author) {
    return null;
  }

  const headline = author?.tagline
    ? intl.formatMessage(
        {
          id: 'ARTICLES_ARTICLE_VIEWER_WRITES_ABOUT',
        },
        {
          title: author.title,
          tagline: author.tagline,
        }
      )
    : author.title;

  const followersWithinContacts =
    author?.metadata.followersWithinContacts.entries || [];
  const followersWithinContactsTotal =
    author?.metadata.followersWithinContacts.total ?? 0;

  const hasFollowersWithinContacts =
    (!isLoggedOut && followersWithinContactsTotal > 0) || false;

  const handleFacePileClick = (): void => {
    setShowFollowersContacts(true);
  };

  return (
    <Styled.AuthorInfoBoxContainer
      display="inline"
      data-qa="article-viewer-author-infobox"
    >
      <Styled.AuthorInfoBoxContentContainer>
        <BodyCopy size="small">{headline}</BodyCopy>

        {author?.description ? (
          <BodyCopy size="small" noMargin>
            {author.description}
          </BodyCopy>
        ) : null}
      </Styled.AuthorInfoBoxContentContainer>

      {hasFollowersWithinContacts ? (
        <Styled.AuthorInfoBoxFacePileContainer>
          <Facepile
            size="small"
            variant="condensed"
            maxActors={MAX_FACEPILE_ACTORS}
            // @ts-expect-error FIXME: SC6
            actors={followersWithinContacts.map((actor, index) => {
              return {
                id: index,
                profileName: actor.pageName,
                type: 'neutral-user',
                src: actor.profileImageURL,
                href: `profile/${actor.pageName}`,
              };
            })}
            data-qa="article-viewer-author-infobox-facepile"
          />

          <Styled.AuthorInfoBoxFacePileMoreContacts
            size="small"
            icon={IconArrowRight}
            iconAfter
            onClick={handleFacePileClick}
          >
            {intl.formatMessage(
              {
                id: getFollowersContactsKey(
                  followersWithinContactsTotal,
                  author.gender || Gender.Other
                ),
              },
              {
                count: followersWithinContactsTotal,
              }
            )}
          </Styled.AuthorInfoBoxFacePileMoreContacts>
        </Styled.AuthorInfoBoxFacePileContainer>
      ) : null}

      <TextButton
        href={getViewArticlesURL(author)}
        icon={IconArrowRight}
        iconAfter
      >
        <FormattedMessage id="ARTICLES_ARTICLE_VIEWER_VIEW_ARTICLES_COLLECTION" />
      </TextButton>

      <ArticleAuthorFollowers
        articleId={articleId}
        author={author}
        isShown={showFollowersContacts ?? false}
        onClose={() => setShowFollowersContacts(false)}
      />
    </Styled.AuthorInfoBoxContainer>
  );
};

export default ArticleAuthorInfoBox;
